import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import MaterialTable, { MTableCell } from 'material-table';
import {
  CircularProgress,
  Drawer,
  IconButton,
  createMuiTheme,
  MuiThemeProvider,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { toast } from '../../../../redux/action';
import {
  Container,
  Header,
  Body,
} from '../../../../Component/Analytics/Components/OpenActivity/OpenActivity.styled';
import { getCombinedDrawerRows } from '../../api';
import { handleDownload } from '../../actions';
import { toCsv } from '../../utils/export';
import { DRAWER_CONFIGS } from './drawerConfigs';
import { OutlinedButton } from '../../MessageDetail/MessageDetail.styled';
import { BLUE } from '../../AnalyticsV2.styled';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

const theme = createMuiTheme({ palette: { secondary: { main: '#1890FF' } } });

// The drawer for a tile on the combined stats page: the contacts and events
// of all the selected messages, with a Message column. The rows are paged and
// searched by andromeda; Export downloads every row.
// ids: the selected message ids. tileKey and channel pick the drawer.
const CombinedDrawer = ({ orgId, ids, tileKey, channel, onClose }) => {
  const dispatch = useDispatch();
  const config = DRAWER_CONFIGS[tileKey](channel);
  const [search, setSearch] = useState('');
  const [isExporting, setExporting] = useState(false);
  // Until the first page arrives the drawer shows a loader instead of the
  // table. The table stays mounted (hidden) because it fetches its own rows;
  // later pages and searches show material-table's own spinner.
  const [hasLoaded, setHasLoaded] = useState(false);

  // material-table's remote data: query.page counts from 0.
  const loadPage = async query => {
    setSearch(query.search);
    try {
      const { result, pagination } = await getCombinedDrawerRows(
        orgId,
        config.kind,
        {
          ...config.params,
          ids,
          search: query.search || undefined,
          page: query.page + 1,
          size: query.pageSize,
        }
      );
      return { data: result, page: query.page, totalCount: pagination.total };
    } catch (e) {
      dispatch(
        toast('error', `Couldn't load ${config.title}. Please try again.`)
      );
      return { data: [], page: 0, totalCount: 0 };
    } finally {
      setHasLoaded(true);
    }
  };

  const exportRows = async () => {
    setExporting(true);
    try {
      const { result } = await getCombinedDrawerRows(orgId, config.kind, {
        ...config.params,
        ids,
        search: search || undefined,
        export: true,
      });
      const csv = toCsv(config.getCsvRows(result));
      handleDownload(
        new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        `${config.title} - ${ids.length} messages.csv`
      );
    } catch (e) {
      dispatch(toast('error', "Couldn't export the rows. Please try again."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Drawer open onClose={onClose} anchor="right">
      <Container>
        <Header>
          <IconButton
            size="small"
            style={{ color: 'white' }}
            aria-label="Close"
            onClick={onClose}
          >
            <Close fontSize="small" />
          </IconButton>
          {config.title}: {ids.length} selected messages
        </Header>
        <Body>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <OutlinedButton onClick={exportRows} disabled={isExporting}>
              {isExporting ? 'Exporting...' : 'Export'}
            </OutlinedButton>
          </div>
          {!hasLoaded && (
            // Centred in the drawer, which is position: fixed.
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress style={{ color: BLUE }} />
            </div>
          )}
          <div style={{ display: hasLoaded ? 'block' : 'none' }}>
            <MuiThemeProvider theme={theme}>
              <MaterialTable
                components={{ Cell: props => <MTableCell {...props} /> }}
                columns={config.columns}
                data={loadPage}
                detailPanel={config.detailPanel}
                onRowClick={
                  config.detailPanel
                    ? (event, row, togglePanel) => togglePanel()
                    : undefined
                }
                options={{
                  showTitle: false,
                  search: true,
                  searchFieldAlignment: 'left',
                  debounceInterval: SEARCH_DEBOUNCE_MS,
                  pageSize: PAGE_SIZE,
                  pageSizeOptions: [],
                  // Without this the table is padded to PAGE_SIZE rows, so the
                  // spinner for a later page or search, centred on the table,
                  // is below the fold.
                  emptyRowsWhenPaging: false,
                  sorting: false,
                  headerStyle: { fontWeight: 'bold' },
                }}
                localization={{
                  body: { emptyDataSourceMessage: config.emptyText },
                  toolbar: { searchPlaceholder: 'Search' },
                }}
              />
            </MuiThemeProvider>
          </div>
        </Body>
      </Container>
    </Drawer>
  );
};

export default CombinedDrawer;
